import supabase from '@/supabase';
import type { StoryMediaType } from '@/types/story';

export const STORIES_MEDIA_BUCKET = 'stories-media';
export const STORIES_THUMBNAILS_BUCKET = 'stories-thumbnails';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_VIDEO_SIZE = 50 * 1024 * 1024;

export interface UploadStoryAssetsResult {
  success: boolean;
  mediaType?: StoryMediaType;
  mediaPath?: string;
  thumbnailPath?: string;
  error?: string;
}

function getStoryMediaType(file: File): StoryMediaType | null {
  if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'image';
  }

  if (ALLOWED_VIDEO_TYPES.includes(file.type)) {
    return 'video';
  }

  return null;
}

function validateStoryFile(file: File): string | null {
  const mediaType = getStoryMediaType(file);

  if (!mediaType) {
    return 'Only JPEG, PNG, GIF, WebP, MP4, and MOV stories are supported.';
  }

  if (mediaType === 'image' && file.size > MAX_IMAGE_SIZE) {
    return 'Image stories must be 10MB or smaller.';
  }

  if (mediaType === 'video' && file.size > MAX_VIDEO_SIZE) {
    return 'Video stories must be 50MB or smaller.';
  }

  return null;
}

async function createVideoThumbnail(file: File): Promise<File> {
  const objectUrl = URL.createObjectURL(file);

  try {
    const video = document.createElement('video');
    video.src = objectUrl;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';

    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error('Unable to read video story.'));
    });

    video.currentTime = Math.min(0.1, video.duration || 0.1);

    await new Promise<void>((resolve, reject) => {
      video.onseeked = () => resolve();
      video.onerror = () => reject(new Error('Unable to seek video story.'));
    });

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Unable to create story thumbnail.');
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((thumbnailBlob) => {
        if (!thumbnailBlob) {
          reject(new Error('Unable to generate story thumbnail.'));
          return;
        }

        resolve(thumbnailBlob);
      }, 'image/jpeg', 0.85);
    });

    return new File([blob], 'story-thumbnail.jpg', { type: 'image/jpeg' });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function uploadStoryAssets(
  file: File,
  userId: string,
  storyId: string
): Promise<UploadStoryAssetsResult> {
  const validationError = validateStoryFile(file);
  if (validationError) {
    return { success: false, error: validationError };
  }

  const mediaType = getStoryMediaType(file);
  if (!mediaType) {
    return { success: false, error: 'Unsupported story media type.' };
  }

  const fileExt = file.name.split('.').pop() || (mediaType === 'image' ? 'jpg' : 'mp4');
  const mediaPath = `${userId}/${storyId}/media.${fileExt}`;
  const thumbnailPath = `${userId}/${storyId}/thumbnail.${mediaType === 'image' ? fileExt : 'jpg'}`;

  try {
    const thumbnailFile = mediaType === 'image' ? file : await createVideoThumbnail(file);

    const [{ error: mediaError }, { error: thumbnailError }] = await Promise.all([
      supabase.storage.from(STORIES_MEDIA_BUCKET).upload(mediaPath, file, {
        cacheControl: '3600',
        upsert: false,
      }),
      supabase.storage.from(STORIES_THUMBNAILS_BUCKET).upload(thumbnailPath, thumbnailFile, {
        cacheControl: '3600',
        upsert: false,
      }),
    ]);

    if (mediaError) {
      return {
        success: false,
        error: `[story-storage:media-upload] ${mediaError.name || 'storage_error'} ${mediaError.message}`,
      };
    }

    if (thumbnailError) {
      return {
        success: false,
        error: `[story-storage:thumbnail-upload] ${thumbnailError.name || 'storage_error'} ${thumbnailError.message}`,
      };
    }

    return {
      success: true,
      mediaType,
      mediaPath,
      thumbnailPath,
    };
  } catch (error) {
    console.error('[uploadStoryAssets] Unexpected error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload story.',
    };
  }
}

export async function createSignedStoryUrl(
  bucket: string,
  path: string,
  expiresInSeconds = 60 * 60
): Promise<string | null> {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresInSeconds);

  if (error) {
    console.error(`[createSignedStoryUrl] Failed for ${bucket}/${path}:`, error);
    return null;
  }

  return data.signedUrl;
}
